import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CustomRangeCalendarProps {
  initialStart?: Date | null;
  initialEnd?: Date | null;
  onApply: (start: Date, end: Date) => void;
  onCancel: () => void;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatShort = (d: Date) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

// A single flexible day-picker range calendar — picking any two dates naturally covers both
// "month to month" and "week to week" filtering, so there's no separate mode to toggle.
// Shared across every "Daily/Weekly/Monthly" period filter in the app (see period-filter.tsx).
export const CustomRangeCalendar: React.FC<CustomRangeCalendarProps> = ({
  initialStart,
  initialEnd,
  onApply,
  onCancel,
}) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(initialStart || today));
  const [rangeStart, setRangeStart] = useState<Date | null>(initialStart || null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(initialEnd || null);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay();
  const isCurrentViewMonth = year === today.getFullYear() && month === today.getMonth();

  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleDayClick = (day: number) => {
    const clicked = new Date(year, month, day);
    if (clicked > today) return;

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(clicked);
      setRangeEnd(null);
      return;
    }

    if (clicked < rangeStart) {
      setRangeEnd(rangeStart);
      setRangeStart(clicked);
    } else {
      setRangeEnd(clicked);
    }
  };

  const isInRange = (day: number) => {
    if (!rangeStart || !rangeEnd) return false;
    const d = new Date(year, month, day);
    return d > rangeStart && d < rangeEnd;
  };

  const isEndpoint = (day: number) => {
    const d = new Date(year, month, day);
    return (!!rangeStart && isSameDay(d, rangeStart)) || (!!rangeEnd && isSameDay(d, rangeEnd));
  };

  return (
    <div className="p-4 w-[300px]">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setViewDate(new Date(year, month - 1, 1))}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <p className="text-sm font-semibold text-[#121212]">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setViewDate(new Date(year, month + 1, 1))}
          disabled={isCurrentViewMonth}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="text-[11px] text-gray-400 text-center font-medium">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const d = new Date(year, month, day);
          const future = d > today;
          const endpoint = isEndpoint(day);
          const inRange = isInRange(day);
          return (
            <button
              type="button"
              key={day}
              disabled={future}
              onClick={() => handleDayClick(day)}
              className={`h-8 w-8 text-xs flex items-center justify-center transition-colors mx-auto rounded-full
                ${future ? 'text-gray-300 cursor-not-allowed' : 'text-[#121212] hover:bg-emerald-50'}
                ${endpoint ? '!bg-emerald-500 !text-white' : ''}
                ${inRange ? 'bg-emerald-50 text-emerald-700 !rounded-none' : ''}
              `}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          {rangeStart ? formatShort(rangeStart) : 'Start'} – {rangeEnd ? formatShort(rangeEnd) : 'End'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-medium text-gray-500 px-3 py-1.5 rounded-full hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => rangeStart && rangeEnd && onApply(rangeStart, rangeEnd)}
            disabled={!rangeStart || !rangeEnd}
            className="text-xs font-semibold text-white bg-black px-3 py-1.5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
